if(!customElements.get("animation-observer")) {
  customElements.define(
    "animation-observer",
    class AnimationObserver extends HTMLElement {
      static get observedAttributes() {
        return [
          "data-intersect-once",
          "data-animation-mobile",
          "data-intersection-observer",
          "data-disable-desktop-animation"
        ];
      }

      constructor() {
        super();
        this.observer = null;
        this.isAnimated = false;
        this.DEFAULT_DELAY = 300;
        this.DEFAULT_DURATION = 900;
        /** @type {MediaQueryList | null} */
        this._desktopMql = null;
        /** Référence stable pour retirer l'écouteur media query */
        this._onDesktopBreakpointChange = this.reconcileAnimationMode.bind(this);
      }

      connectedCallback() {
        // Largeur ≥ 750px : aligné sur les media queries du thème (bannière pleine largeur)
        this._desktopMql = window.matchMedia("(min-width: 750px)");
        this._desktopMql.addEventListener("change", this._onDesktopBreakpointChange);
        this.reconcileAnimationMode();
      }

      disconnectedCallback() {
        if (this._desktopMql) {
          this._desktopMql.removeEventListener("change", this._onDesktopBreakpointChange);
          this._desktopMql = null;
        }
        this.teardownObserver();
      }

      attributeChangedCallback(name, oldValue, newValue) {
        if (oldValue === newValue) {
          return;
        }
        this.reconcileAnimationMode();
      }

      parseIntSafe(value, fallback) {
        const parsed = parseInt(value);
        return isNaN(parsed) ? fallback : parsed;
      }

      /**
       * Indique si le thème demande un affichage immédiat sans scroll-in sur desktop.
       */
      shouldApplyInstantDesktopState() {
        return (
          this.dataset.disableDesktopAnimation === "true" &&
          window.matchMedia("(min-width: 750px)").matches
        );
      }

      /**
       * Retire les classes d'état final pour pouvoir réinitialiser (ex. changement de breakpoint).
       */
      clearInstantAnimationState() {
        this.classList.remove("isAnimated", "inAnimation");
        this.querySelectorAll(".full-width-banner__animation").forEach(element => {
          element.classList.remove("isAnimated", "inAnimation");
        });
      }

      teardownObserver() {
        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }
      }

      /**
       * Applique l'état « animé terminé » sans délais (desktop + option thème).
       */
      applyInstantDesktopAnimationState() {
        const animationElements = this.querySelectorAll(
          ".full-width-banner__animation"
        );

        if (!animationElements.length) {
          this.classList.add("isAnimated");
          return;
        }

        animationElements.forEach(element => {
          element.classList.add("isAnimated");
        });
        this.classList.add("isAnimated");
      }

      /**
       * Choisit entre observer avec scroll ou état statique desktop selon les réglages.
       */
      reconcileAnimationMode() {
        this.teardownObserver();

        if (this.shouldApplyInstantDesktopState()) {
          this.clearInstantAnimationState();
          this.applyInstantDesktopAnimationState();
          return;
        }

        this.clearInstantAnimationState();
        this.initObserver();
      }

      initObserver() {
        const observerElement = this;
        const intersectOnce =
          observerElement.dataset.intersectOnce !== "false";
        const observerOptions = {
          rootMargin: "0px 0px -50% 0px",
          threshold: 0,
          ...this.getIntersectionOptions()
        };

        this.observer = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (!entry.isIntersecting) {
              return;
            }

            const allowMobileAnimation = observerElement.dataset.animationMobile !== "false";
            if (DeviceDetector.isMobile() && !allowMobileAnimation) {
              return;
            }

            if (this.isAnimated && intersectOnce) {
              return;
            }

            this.isAnimated = true;
            this.triggerAnimation();

            if (intersectOnce) {
              this.observer.unobserve(observerElement);
            }
          });
        }, observerOptions);

        this.observer.observe(observerElement);
      }

      getIntersectionOptions() {
        try {
          return JSON.parse(this.dataset.intersectionObserver || "{}");
        } catch (e) {
          console.error("Invalid JSON in data-intersection-observer:", e);
          return {};
        }
      }

      triggerAnimation() {
        const animationBody = this.querySelector(
          ".full-width-banner__animation--body"
        );
        const animationElements = this.querySelectorAll(
          ".full-width-banner__animation"
        );

        this.addEventListener(
          "animationend",
          () => {
            this.isAnimated = false;
          },
          { once: true }
        );

        if (!animationElements.length) {
          this.classList.add("isAnimated");
          return;
        }

        const baseAnimationDelay = animationBody
          ? this.parseIntSafe(
              animationBody.dataset.animationDelay,
              this.DEFAULT_DELAY
            )
          : this.DEFAULT_DELAY;
        const baseAnimationDuration = animationBody
          ? this.parseIntSafe(
              animationBody.dataset.animationDuration,
              this.DEFAULT_DURATION
            )
          : this.DEFAULT_DURATION;

        this.classList.add("inAnimation");

        let completedAnimations = 0;
        const totalAnimations = animationElements.length;

        animationElements.forEach((element, i) => {
          const elementDelay = this.parseIntSafe(
            element.dataset.animationDelay,
            baseAnimationDelay
          );
          const elementDuration = this.parseIntSafe(
            element.dataset.animationDuration,
            baseAnimationDuration
          );
          const startDelay = i * (elementDelay / 2);

          setTimeout(() => {
            element.classList.add("inAnimation");
            const childElement = element.querySelector("*");
            if (childElement) {
              childElement.style.animationDuration = `${elementDuration}ms`;
            }
          }, startDelay);

          setTimeout(() => {
            element.classList.remove("inAnimation");
            element.classList.add("isAnimated");

            completedAnimations++;

            if (completedAnimations === totalAnimations) {
              this.classList.remove("inAnimation");
              this.classList.add("isAnimated");
              this.isAnimated = true;
            }
          }, startDelay + baseAnimationDuration);
        });
      }
    }
  );
}
